import { Suspense, Fragment, lazy } from 'react';
import { Switch, Route, Redirect, matchPath } from 'react-router-dom';

import { isProd } from 'src/config/index';
import SplashScreen from 'src/components/SplashScreen';
import { ROUTER_NAME } from 'src/constants';

const AuthProvider = lazy(() => import('src/contexts/JWTAuthContext'));

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<SplashScreen />}>
    <Switch>
      {routes.filter(Boolean).map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard {...(route.guard && { match: props.match })}>
                <Layout>
                  {route.routes ? <Switch>{renderRoutes(route.routes)}</Switch> : <Component {...props} />}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const iframeRoutes = [
  {
    path: ROUTER_NAME.SUBSCRIPTION + '/404',
    component: lazy(() => import('src/views/subscription/notFound'))
  },
  !isProd && {
    exact: true,
    path: ROUTER_NAME.SUBSCRIPTION_V2 + '/demo',
    component: lazy(() => import('src/views/subscription/v2/demo/index.tsx'))
  },
  {
    exact: true,
    path: ROUTER_NAME.SUBSCRIPTION_MOBILE_PAYWALL,
    component: lazy(() => import('src/views/subscription/mobile/index.tsx'))
  },
  {
    path: ROUTER_NAME.SUBSCRIPTION_V2,
    routes: [
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_V2 + '/manage',
        component: lazy(() => import('src/views/subscription/v2/manage'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_V2 + '/confirmation/:productId/:priceId',
        component: lazy(() => import('src/views/subscription/v2/manage/confirmation'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_V2 + '/paymentDetails/:productId/:priceId',
        component: lazy(() => import('src/views/subscription/v2/manage/paymentDetails'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_V2 + '/payment-method/:productId?',
        component: lazy(() => import('src/views/subscription/v2/paymentMethod'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_V2 + '/seat-management',
        component: lazy(() => import('src/views/subscription/v2/seatManagement'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_V2 + '/invoice-history',
        component: lazy(() => import('src/views/subscription/v2/invoiceHistory'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_V2 + '/card-update',
        component: lazy(() => import('src/views/subscription/v2/cardUpdate')),
        layout: lazy(() => import('src/components/BookipiBdsThemeProvider'))
      },
      {
        component: () => <Redirect to={ROUTER_NAME.SUBSCRIPTION + '/404'} />
      }
    ]
  },
  {
    path: ROUTER_NAME.SUBSCRIPTION_EDIT,
    component: lazy(() => import('src/views/subscription/edit'))
  },
  {
    exact: true,
    path: ROUTER_NAME.SUBSCRIPTION + '/discount-campaign/:code?',
    component: lazy(() => import('src/views/subscription/v2/discountComponents/discountCampaign'))
  },
  {
    path: ROUTER_NAME.SUBSCRIPTION,
    component: lazy(() => import('src/views/subscription')),
    routes: [
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION,
        component: lazy(() => import('src/views/subscription'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_PAYMENT_METHOD,
        component: lazy(() => import('src/views/subscription/paymentMethod'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_PAYMENT_METHOD + '/:id?/:productId?',
        component: lazy(() => import('src/views/subscription/paymentMethodEdit'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_PAYMENT_METHOD + '/v2/new/:productId?',
        component: lazy(() => import('src/views/subscription/v2/paymentMethod'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_INVOICE_HISTORY + '/:id?',
        component: lazy(() => import('src/views/subscription/invoiceHistory'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_ORDER + '/:productId?',
        component: lazy(() => import('src/views/subscription/order'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_UPDATE + '/:productId/:priceId',
        component: lazy(() => import('src/views/subscription/update'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION + '/success',
        component: lazy(() => import('src/views/subscription/success'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_CONFIRM_CANCEL,
        component: lazy(() => import('src/views/subscription/confirmCancel'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_CANCEL,
        component: lazy(() => import('src/views/subscription/subscriptionTab/cancelSubscription'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_CANCEL_SURVEY,
        component: lazy(() => import('src/views/subscription/cancelSurvey'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_CANCEL_SUCCESS,
        component: lazy(() => import('src/views/subscription/cancelSuccess'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_DOWNGRADE_SURVEY,
        component: lazy(() => import('src/views/subscription/downgradeSurvey'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_REACTIVATE,
        component: lazy(() => import('src/views/subscription/reactivate'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SUBSCRIPTION_SESSION_EXPIRED,
        component: lazy(() => import('src/views/subscription/expired'))
      },
      {
        component: () => <Redirect to={ROUTER_NAME.SUBSCRIPTION + '/404'} />
      }
    ]
  }
];

const routes = [
  {
    exact: true,
    path: ROUTER_NAME.NOT_FOUND,
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  {
    exact: true,
    guard: lazy(() => import('src/components/GuestGuard')),
    path: ROUTER_NAME.LOGIN,
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    guard: lazy(() => import('src/components/AuthGuard')),
    path: ROUTER_NAME.ONBOARD,
    component: lazy(() => import('src/views/on-board'))
  },
  {
    exact: true,
    guard: lazy(() => import('src/components/AuthGuard')),
    path: '/onboard/globalpay/:companyId',
    component: lazy(() => import('src/views/on-board/OnBoardUSView'))
  },
  {
    exact: true,
    guard: lazy(() => import('src/components/AuthGuard')),
    path: ROUTER_NAME.ONBOARD_WITH_PROVIDER_COMPANY,
    component: lazy(() => import('src/views/on-board'))
  },
  {
    exact: true,
    guard: lazy(() => import('src/components/AuthGuard')),
    path: ROUTER_NAME.ONBOARD_US,
    component: lazy(() => import('src/views/on-board/OnBoardUSView'))
  },
  {
    exact: true,
    guard: lazy(() => import('src/components/GuestGuard')),
    path: ROUTER_NAME.ONBOARD_US + '/:provider/:companyId',
    component: lazy(() => import('src/views/on-board/OnBoardUSView'))
  },
  {
    exact: true,
    guard: lazy(() => import('src/components/AuthGuard')),
    path: ROUTER_NAME.ONBOARD_AUS,
    component: lazy(() => import('src/views/on-board/OnBoardAUSView'))
  },
  {
    exact: true,
    guard: lazy(() => import('src/components/GuestGuard')),
    path: ROUTER_NAME.ONBOARD_AUS + '/:provider/:companyId',
    component: lazy(() => import('src/views/on-board/OnBoardAUSView'))
  },
  {
    guard: lazy(() => import('src/components/GuestGuard')),
    path: ROUTER_NAME.DOWNLOAD_QR,
    component: lazy(() => import('src/views/vendorBank/downloadqr'))
  },
  {
    guard: lazy(() => import('src/components/GuestGuard')),
    path: ROUTER_NAME.DOWNLOAD,
    component: lazy(() => import('src/views/vendorBank/download'))
  },
  {
    guard: lazy(() => import('src/components/GuestGuard')),
    path: ROUTER_NAME.VENDOR_BANK + '/:token/:stage?',
    component: lazy(() => import('src/views/vendorBank'))
  },
  ...iframeRoutes,
  {
    path: ROUTER_NAME.PAYMENT_LINK_VIEW,
    component: lazy(() => import('src/views/paymentLinks/view'))
  },
  {
    path: ROUTER_NAME.PAYMENT_LINK,
    routes: [
      {
        exact: true,
        path: ROUTER_NAME.PAYMENT_LINK_LIST,
        component: lazy(() => import('src/views/paymentLinks/list'))
      },
      {
        exact: true,
        path: ROUTER_NAME.PAYMENT_LINK_NEW,
        component: lazy(() => import('src/views/paymentLinks/modals/new'))
      },
      {
        exact: true,
        path: ROUTER_NAME.PAYMENT_LINK_SHARE,
        component: lazy(() => import('src/views/paymentLinks/modals/share'))
      },
      {
        exact: true,
        path: ROUTER_NAME.PAYMENT_LINK_EDIT,
        component: lazy(() => import('src/views/paymentLinks/modals/edit'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: ROUTER_NAME.ALL,
    guard: lazy(() => import('src/components/AuthGuard')),
    layout: lazy(() => import('src/layouts/MainLayoutV2')),
    routes: [
      {
        exact: true,
        path: ROUTER_NAME.DEFAULT,
        component: lazy(() => import('src/views/Transactions'))
      },
      {
        exact: true,
        path: ROUTER_NAME.TRANSACTION_DETAIL,
        component: lazy(() => import('src/views/Transactions/detail'))
      },
      {
        exact: true,
        path: ROUTER_NAME.PAYOUTS,
        component: lazy(() => import('src/views/Payouts'))
      },
      {
        exact: true,
        path: '/globalPay',
        component: lazy(() => import('src/views/globalPay'))
      },
      {
        exact: true,
        path: '/stripe',
        component: lazy(() => import('src/views/stripe'))
      },
      {
        exact: true,
        path: '/globalPaySetting',
        component: lazy(() => import('src/views/globalPaySetting'))
      },
      {
        exact: true,
        path: ROUTER_NAME.DISPUTES,
        component: lazy(() => import('src/views/Disputes'))
      },
      {
        exact: true,
        path: ROUTER_NAME.TRANSACTIONS,
        component: lazy(() => import('src/views/Transactions'))
      },

      {
        exact: true,
        path: ROUTER_NAME.PAY,
        component: lazy(() => import('src/views/pay/PayView'))
      },
      {
        exact: true,
        path: ROUTER_NAME.GET_PAID,
        component: lazy(() => import('src/views/get-paid/GetPaidView'))
      },
      {
        exact: true,
        path: ROUTER_NAME.PEOPLE,
        component: lazy(() => import('src/views/people/PeopleView'))
      },
      {
        exact: true,
        path: ROUTER_NAME.INTEGRATION,
        component: lazy(() => import('src/views/integration/IntegrationView'))
      },
      {
        exact: true,
        path: ROUTER_NAME.SETTINGS,
        component: lazy(() => import('src/views/settings/SettingsView'))
      },
      {
        exact: true,
        path: ROUTER_NAME.ACCEPT_INVITE + '/:companyId',
        component: lazy(() => import('src/views/company/CompanyView/AcceptInvite'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

export default function () {
  return (
    <Suspense fallback={<SplashScreen />}>
      <AuthProvider>{renderRoutes(routes)}</AuthProvider>
    </Suspense>
  );
}

export function isIframe(routes = iframeRoutes) {
  return routes.some((route) =>
    route.routes ? isIframe(route.routes) : matchPath(window.location.pathname, route.path)
  );
}
